import { useState, useEffect } from "react";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import { Save, Settings as SettingsIcon, Webhook, Link2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminSettings() {
  const { getAuthHeaders } = useAuth();
  const [settings, setSettings] = useState({
    program_enabled: true,
    affiliate_share_pct: 10,
    admin_share_pct: 10,
    maaly_api_base_url: "",
    maaly_webhook_secret: "",
    signup_base_url: "https://maaly.io/signup",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/settings`, {
        credentials: "include",
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        setSettings(await response.json());
      }
    } catch (error) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/settings`, {
        method: "PATCH",
        credentials: "include",
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(settings),
      });

      if (response.ok) {
        toast.success("Settings saved!");
      } else {
        toast.error("Failed to save settings");
      }
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Settings" subtitle="Configure the affiliate program">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Settings" subtitle="Configure the affiliate program">
      <div data-testid="admin-settings" className="space-y-6 max-w-2xl">
        {/* Program Settings */}
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <SettingsIcon className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Program Settings</h3>
              <p className="text-sm text-muted-foreground">General program configuration</p>
            </div>
          </div>

          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-foreground">Program Enabled</Label>
                <p className="text-sm text-muted-foreground">Allow new affiliate signups and referrals</p>
              </div>
              <Switch
                checked={settings.program_enabled}
                onCheckedChange={(checked) =>
                  setSettings({ ...settings, program_enabled: checked })
                }
                data-testid="program-enabled-toggle"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-muted-foreground">Affiliate Share (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={settings.affiliate_share_pct}
                  onChange={(e) =>
                    setSettings({ ...settings, affiliate_share_pct: parseFloat(e.target.value) })
                  }
                  data-testid="affiliate-share-input"
                  className="bg-background border-border text-foreground"
                />
                <p className="text-xs text-slate-500">Percentage of fees paid to affiliates</p>
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Admin Share (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={settings.admin_share_pct}
                  onChange={(e) =>
                    setSettings({ ...settings, admin_share_pct: parseFloat(e.target.value) })
                  }
                  data-testid="admin-share-input"
                  className="bg-background border-border text-foreground"
                />
                <p className="text-xs text-slate-500">Percentage retained by admin</p>
              </div>
            </div>
          </div>
        </div>

        {/* Integration Settings */}
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
              <Webhook className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Maaly Integration</h3>
              <p className="text-sm text-muted-foreground">Configure webhook and API settings</p>
            </div>
          </div>

          <div className="space-y-6">
            <div className="space-y-2">
              <Label className="text-muted-foreground">Maaly API Base URL</Label>
              <Input
                value={settings.maaly_api_base_url || ""}
                onChange={(e) =>
                  setSettings({ ...settings, maaly_api_base_url: e.target.value })
                }
                placeholder="https://api.maaly.io"
                className="bg-background border-border text-foreground"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-muted-foreground">Webhook Secret</Label>
              <Input
                type="password"
                value={settings.maaly_webhook_secret || ""}
                onChange={(e) =>
                  setSettings({ ...settings, maaly_webhook_secret: e.target.value })
                }
                placeholder="Enter webhook secret"
                className="bg-background border-border text-foreground"
              />
              <p className="text-xs text-slate-500">Used to verify webhook signatures from Maaly</p>
            </div>
          </div>
        </div>

        {/* Referral Settings */}
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
              <Link2 className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Referral Links</h3>
              <p className="text-sm text-muted-foreground">Configure referral link destinations</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-muted-foreground">Signup Base URL</Label>
            <Input
              value={settings.signup_base_url || ""}
              onChange={(e) =>
                setSettings({ ...settings, signup_base_url: e.target.value })
              }
              placeholder="https://maaly.io/signup"
              data-testid="signup-base-url-input"
              className="bg-background border-border text-foreground"
            />
            <p className="text-xs text-slate-500">
              Base URL for referral links (ref parameter will be appended)
            </p>
          </div>
        </div>

        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={saving}
          data-testid="save-settings-btn"
          className="w-full glow-primary"
        >
          {saving ? (
            <span className="flex items-center gap-2">
              <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              Saving...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Save className="w-4 h-4" /> Save Settings
            </span>
          )}
        </Button>
      </div>
    </DashboardLayout>
  );
}
